import React, { useEffect, useState, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../../api/axios';
import { FiTruck, FiPrinter, FiSend, FiCheck } from 'react-icons/fi';
import { dt, DashLang } from '../../utils/dashboardTranslations';
import { useAuthStore } from '../../store/authStore';

const statuses = ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'delivered', 'cancelled'];

export default function Deliveries() {
  const { store, lang, myPerms } = useOutletContext<{ store: any; lang: DashLang; myPerms: any }>();
  const { user } = useAuthStore();
  const [orders, setOrders] = useState<any[]>([]);
  const [drivers, setDrivers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [filterDriver, setFilterDriver] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showDispatch, setShowDispatch] = useState<any>(null);
  const [selectedDriver, setSelectedDriver] = useState('');
  const [dispatching, setDispatching] = useState(false);
  const printRef = useRef<HTMLDivElement>(null);

  // Check if user only has view_own (not full view) for deliveries
  const isOwner = myPerms?.is_owner;
  const deliveryPerms = myPerms?.permissions?.deliveries || {};
  const hasFullView = isOwner || !!deliveryPerms?.view;
  const hasEdit = isOwner || !!deliveryPerms?.edit;
  const viewOwnOnly = !hasFullView && !!deliveryPerms?.view_own;

  useEffect(() => {
    if (store?.id) { loadOrders(); loadDrivers(); }
  }, [store?.id, filterDriver, filterStatus, page]);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      if (viewOwnOnly) {
        params.set('mine', '1');
      } else if (filterDriver === 'mine') {
        params.set('mine', '1');
      } else if (filterDriver) {
        params.set('driver_id', filterDriver);
      }
      if (filterStatus) params.set('status', filterStatus);
      const res = await api.get(`/stores/${store.id}/deliveries?${params}`);
      if (page === 1) setOrders(res.data.data || []);
      else setOrders(prev => [...prev, ...(res.data.data || [])]);
      setLastPage(res.data.last_page || 1);
    } finally { setLoading(false); }
  };

  const loadDrivers = async () => {
    try {
      const res = await api.get(`/stores/${store.id}/drivers`);
      setDrivers(res.data || []);
    } catch {}
  };

  const handleDispatch = async () => {
    if (!showDispatch || !selectedDriver) return;
    setDispatching(true);
    try {
      await api.post(`/stores/${store.id}/orders/${showDispatch.id}/dispatch`, { driver_id: selectedDriver });
      toast.success(dt('dispatch_success', lang));
      setShowDispatch(null);
      setSelectedDriver('');
      setPage(1);
      loadOrders();
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Error');
    } finally { setDispatching(false); }
  };

  const updateStatus = async (orderId: number, status: string) => {
    try {
      await api.put(`/stores/${store.id}/orders/${orderId}/status`, { status });
      toast.success(dt('order_status_updated', lang));
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, status } : o));
    } catch {
      toast.error(dt('error_updating_status', lang));
    }
  };

  const getDriverName = (driverId: number | null) => {
    if (!driverId) return dt('unassigned', lang);
    const d = drivers.find(d => d.id === driverId);
    return d?.name || `#${driverId}`;
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const rows = orders.map(o => `
      <tr>
        <td>${o.order_number}</td>
        <td>${o.customer_name}<br/><small>${o.customer_phone}</small></td>
        <td style="max-width:200px">${o.delivery_address || '-'}</td>
        <td>${o.items?.map((i: any) => `${i.product_name} x${i.quantity}`).join('<br/>') || '-'}</td>
        <td>${store.currency_symbol} ${Number(o.total).toFixed(3)}</td>
        <td>${getDriverName(o.driver_id)}</td>
        <td>${dt(o.status, lang)}</td>
      </tr>
    `).join('');

    printWindow.document.write(`
      <html dir="${lang === 'ar' ? 'rtl' : 'ltr'}">
      <head><title>${dt('print_delivery_list', lang)} - ${store.name}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; font-size: 13px; }
        h2 { margin-bottom: 4px; }
        table { width: 100%; border-collapse: collapse; margin-top: 16px; }
        th, td { border: 1px solid #ccc; padding: 8px; text-align: ${lang === 'ar' ? 'right' : 'left'}; }
        th { background: #f3f4f6; font-weight: 600; }
        small { color: #666; }
        @media print { button { display: none; } }
      </style></head>
      <body>
        <h2>${store.name}</h2>
        <p>${dt('print_delivery_list', lang)} - ${new Date().toLocaleDateString(lang === 'ar' ? 'ar-KW' : 'en-US')}</p>
        <table>
          <thead><tr>
            <th>${dt('order_number', lang)}</th>
            <th>${dt('customer', lang)}</th>
            <th>${dt('address', lang)}</th>
            <th>${dt('item', lang)}</th>
            <th>${dt('total', lang)}</th>
            <th>${dt('driver', lang)}</th>
            <th>${dt('status', lang)}</th>
          </tr></thead>
          <tbody>${rows}</tbody>
        </table>
        <script>window.print();</script>
      </body></html>
    `);
    printWindow.document.close();
  };

  if (!store) return null;

  return (
    <div>
      <div className="page-header">
        <h1>{dt('deliveries', lang)}</h1>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {!viewOwnOnly && (
            <select className="form-input" style={{ width: 'auto' }} value={filterDriver} onChange={e => { setFilterDriver(e.target.value); setPage(1); }}>
              <option value="">{dt('all_deliveries', lang)}</option>
              <option value="mine">{dt('my_deliveries', lang)}</option>
              <option value="unassigned">{dt('unassigned', lang)}</option>
              {drivers.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          )}
          <select className="form-input" style={{ width: 'auto' }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1); }}>
            <option value="">{dt('all_orders', lang)}</option>
            {statuses.map(s => <option key={s} value={s}>{dt(s, lang)}</option>)}
          </select>
          {!viewOwnOnly && (
            <button className="btn btn-white" onClick={handlePrint} title={dt('print_delivery_list', lang)}>
              <FiPrinter size={16} />
            </button>
          )}
        </div>
      </div>

      {loading && page === 1 ? (
        <div className="empty-state"><p>{dt('loading', lang)}...</p></div>
      ) : orders.length === 0 ? (
        <div className="empty-state">
          <FiTruck size={40} />
          <h3>{dt('no_deliveries', lang)}</h3>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>{dt('order_number', lang)}</th>
                  <th>{dt('customer', lang)}</th>
                  <th>{dt('address', lang)}</th>
                  <th>{dt('total', lang)}</th>
                  {!viewOwnOnly && <th>{dt('driver', lang)}</th>}
                  <th>{dt('status', lang)}</th>
                  {hasEdit && <th>{dt('actions', lang)}</th>}
                </tr>
              </thead>
              <tbody>
                {orders.map(order => (
                  <tr key={order.id}>
                    <td><strong>{order.order_number}</strong></td>
                    <td>
                      <div>{order.customer_name}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{order.customer_phone}</div>
                    </td>
                    <td style={{ maxWidth: 200, fontSize: 13 }}>{order.delivery_address || '-'}</td>
                    <td><strong>{store.currency_symbol} {Number(order.total).toFixed(3)}</strong></td>
                    {!viewOwnOnly && (
                      <td>
                        <span style={{
                          padding: '3px 10px', borderRadius: 50, fontSize: 11, fontWeight: 600,
                          background: order.driver_id ? '#dcfce7' : '#fef3c7',
                          color: order.driver_id ? '#16a34a' : '#d97706',
                        }}>
                          {getDriverName(order.driver_id)}
                        </span>
                      </td>
                    )}
                    <td>
                      {hasEdit ? (
                        <select
                          className="form-input"
                          value={order.status}
                          onChange={e => updateStatus(order.id, e.target.value)}
                          style={{ padding: '6px 10px', fontSize: 12, width: 'auto' }}
                        >
                          {statuses.map(s => <option key={s} value={s}>{dt(s, lang)}</option>)}
                        </select>
                      ) : (
                        <span style={{
                          padding: '3px 10px', borderRadius: 50, fontSize: 11, fontWeight: 600,
                          background: '#f3f4f6', color: '#374151',
                        }}>
                          {dt(order.status, lang)}
                        </span>
                      )}
                    </td>
                    {hasEdit && (
                      <td style={{ display: 'flex', gap: 4 }}>
                        {!viewOwnOnly && (
                          <button
                            className="btn btn-sm btn-primary"
                            onClick={() => { setShowDispatch(order); setSelectedDriver(order.driver_id || ''); }}
                            title={dt('dispatch', lang)}
                          >
                            <FiSend size={14} />
                          </button>
                        )}
                        {order.status !== 'delivered' && order.status !== 'cancelled' && (
                          <button
                            className="btn btn-sm"
                            style={{ background: '#dcfce7', color: '#16a34a', border: 'none' }}
                            onClick={() => updateStatus(order.id, 'delivered')}
                            title={dt('delivered', lang)}
                          >
                            <FiCheck size={14} />
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {page < lastPage && (
            <div style={{ textAlign: 'center', marginTop: 16 }}>
              <button className="btn btn-white" onClick={() => setPage(page + 1)} disabled={loading}>
                {loading ? '...' : dt('load_more', lang)}
              </button>
            </div>
          )}
        </>
      )}

      {/* Dispatch Modal */}
      {showDispatch && (
        <div className="modal-overlay" onClick={() => setShowDispatch(null)}>
          <div className="modal" style={{ maxWidth: 450 }} onClick={e => e.stopPropagation()}>
            <h2>{dt('dispatch', lang)} - {showDispatch.order_number}</h2>

            <div style={{ background: '#f9fafb', borderRadius: 10, padding: 14, marginBottom: 16, fontSize: 13 }}>
              <p><strong>{dt('customer', lang)}:</strong> {showDispatch.customer_name}</p>
              <p><strong>{dt('phone', lang)}:</strong> {showDispatch.customer_phone}</p>
              <p><strong>{dt('address', lang)}:</strong> {showDispatch.delivery_address || '-'}</p>
              {showDispatch.items?.map((item: any) => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
                  <span>{item.product_name} x{item.quantity}</span>
                  <span>{store.currency_symbol} {Number(item.total).toFixed(3)}</span>
                </div>
              ))}
              <div style={{ borderTop: '1px solid #e5e7eb', marginTop: 8, paddingTop: 8, fontWeight: 700 }}>
                {dt('total', lang)}: {store.currency_symbol} {Number(showDispatch.total).toFixed(3)}
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">{dt('assign_driver', lang)}</label>
              <select className="form-input" value={selectedDriver} onChange={e => setSelectedDriver(e.target.value)}>
                <option value="">{dt('select_driver', lang)}</option>
                {drivers.map(d => <option key={d.id} value={d.id}>{d.name} {d.phone ? `(${d.phone})` : ''}</option>)}
              </select>
            </div>

            <div className="modal-actions">
              <button className="btn btn-white" onClick={() => setShowDispatch(null)}>{dt('cancel', lang)}</button>
              <button className="btn btn-primary" onClick={handleDispatch} disabled={dispatching || !selectedDriver}>
                <FiSend size={14} style={{ marginInlineEnd: 4 }} />
                {dispatching ? '...' : dt('dispatch', lang)}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
